import React from "react";
import { Navigate } from "react-router-dom";
import Loading from "../Loading";
import SignInRedirect from "../pages/Auth/SignInRedirect";
import SignOutRedirect from "../pages/Auth/SignOutRedirect";
import MyBag from "../Checkout/MyBag";
import HomeOutlet from "../pages/AppPages/Home/HomeOutlet";
import Home from "../pages/AppPages/Home";
import ShopOutlet from "../pages/AppPages/Shop/ShopOutlet";
import SearchOutlet from "../pages/AppPages/Search/SearchOutlet";
import Concierge from "../pages/AppPages/Concierge";
import { AppRoutesWithChildrenProps } from "./routeBuilder";
import Search from "../pages/AppPages/Search";

const ConciergeOutlet = React.lazy(
  () => import("../pages/AppPages/Concierge/ConciergeOutlet")
);
const Shop = React.lazy(() => import("../pages/AppPages/Shop"));
const AffiliateLanding = React.lazy(
  () => import("../pages/AppPages/Pages/Affiliate/landing")
);
const BuyItAgainLanding = React.lazy(
  () => import("../pages/AppPages/Pages/BuyAgain/landing")
);
const ClubInvitesLanding = React.lazy(
  () => import("../pages/AppPages/Pages/ClubInvites/landing")
);
const ContentLanding = React.lazy(
  () => import("../pages/AppPages/Pages/Content/landing")
);
const DailyDealsLanding = React.lazy(
  () => import("../pages/AppPages/Pages/DailyDeals/landing")
);
const InstantRewardsLanding = React.lazy(
  () => import("../pages/AppPages/Pages/InstantRewards/landing")
);
const MyClubLanding = React.lazy(
  () => import("../pages/AppPages/Pages/MyClubs/landing")
);
const RecentlyViewedLanding = React.lazy(
  () => import("../pages/AppPages/Pages/RecentlyViewed/landing")
);
const ReviewLanding = React.lazy(
  () => import("../pages/AppPages/Pages/Review/landing")
);
const SurveyLanding = React.lazy(
  () => import("../pages/AppPages/Pages/Survey/landing")
);
const TopPickLanding = React.lazy(
  () => import("../pages/AppPages/Pages/TopPicks/landing")
);
const TrendingLanding = React.lazy(
  () => import("../pages/AppPages/Pages/Trending/landing")
);
const DepartmentLanding = React.lazy(
  () => import("../pages/AppPages/Pages/Departments/landing")
);
const DepartmentLandingDetail = React.lazy(
  () => import("../pages/AppPages/Pages/Departments/DepartmentLandingDetail")
);
const BrandStoreLandingPage = React.lazy(
  () => import("../pages/AppPages/BrandStore/BrandStoreLandingPage")
);
const ScanGetAppQRCode = React.lazy(() => import("../ScanGetAppQRCode"));
const StoreLocatorTestPage = React.lazy(
  () => import("../pages/AppPages/Pages/StoreLocatorTest")
);
const SearchQueryPillTestPage = React.lazy(
  () => import("../pages/AppPages/Pages/SearchQueryPillTest")
);
const chatHistoryImport = () => import("../pages/AppPages/Concierge/ChatHistory");
const ChatHistory = React.lazy(chatHistoryImport);
export { chatHistoryImport as preloadChatHistory };

export const fallbackRoutes: AppRoutesWithChildrenProps[] = [
  { path: "signinredirect", element: <SignInRedirect /> },
  { path: "signoutredirect", element: <SignOutRedirect /> },
  { path: "scangetappqrcode", element: <ScanGetAppQRCode /> },
  { path: "loading", element: <Loading storeSpin fullscreen /> },
  { path: "*", element: <Navigate to="" /> },
];

const departmentRoutes: AppRoutesWithChildrenProps[] = [
  { path: "department/:departmentId", element: <DepartmentLanding /> },
  {
    path: "department/:departmentId/instant",
    element: <DepartmentLandingDetail type={"instant"} />,
  },
  {
    path: "department/:departmentId/best",
    element: <DepartmentLandingDetail type={"best"} />,
  },
  {
    path: "department/:departmentId/popular",
    element: <DepartmentLandingDetail type={"popular"} />,
  },
];

/**
 * Routes for brandstore pages of the brandclub universal store
 */
export const brandstoreLandingRoutes: AppRoutesWithChildrenProps[] = [
  {
    path: ":brandName/b/:brandId/*",
    element: <BrandStoreLandingPage />,
    pageType: "BrandStoreSummary",
    pageId: "BrandStoreSummary",
  },
  {
    path: ":title/p/:sku/*",
    element: <BrandStoreLandingPage />,
    pageType: "Catalog",
    pageId: ":sku",
  },
];
export const dtcCommonAppPageRoutes: AppRoutesWithChildrenProps[] = [
  {
    path: "mybag/",
    element: <MyBag />,
    pageType: "MyBag",
    pageId: "MyBag",
    handle: {
      pageNavConfig: {
        hasHamburgerMenu: true,
      },
    },
  },
];

/**
 * Full-screen product page route for DTC stores.
 * Rendered outside StoreSinglePageOutlet so custom pages
 * (published or preview) are shown without the store header/footer chrome.
 * Falls back to the default PDP widgets when no custom page exists.
 */
export const dtcFullScreenProductRoutes: AppRoutesWithChildrenProps[] = [
  {
    path: ":title/p/:sku/*",
    element: <BrandStoreLandingPage />,
    pageType: "Catalog",
    pageId: ":sku",
  },
];

/**
 * Concierge route for DTC stores (same full-page concierge as Universal, under DTC layout).
 */
export const dtcConciergeRoutes: AppRoutesWithChildrenProps[] = [
  {
    path: "concierge/",
    element: <ConciergeOutlet />,
    children: [
      {
        path: "",
        element: <Concierge />,
        pageType: "Concierge",
        pageId: "Concierge",
      },
      {
        path: "history",
        element: <ChatHistory />,
        pageType: "Concierge",
        pageId: "ChatHistory",
      },
    ],
  },
];

export const commonAppPageRoutes: AppRoutesWithChildrenProps[] = [
  {
    path: "mybag/",
    element: <MyBag />,
    pageType: "MyBag",
    pageId: "MyBag",
  },
  {
    path: "store-locator-test/",
    element: <StoreLocatorTestPage />,
    pageType: "StoreLocatorTest",
    pageId: "StoreLocatorTest",
  },
  {
    path: "search-query-pill-test/",
    element: <SearchQueryPillTestPage />,
    pageType: "SearchQueryPillTest",
    pageId: "SearchQueryPillTest",
  },
  {
    path: "dailydeals/",
    element: <DailyDealsLanding />,
    pageType: "DailyDeals",
    pageId: "DailyDeals",
  },
  {
    path: "surveys/",
    element: <SurveyLanding />,
    pageType: "Survey",
    pageId: "SurveySummary",
  },
  { path: "myclubs/", element: <MyClubLanding /> },
  {
    path: "contentrewards/",
    element: <ContentLanding />,
    pageType: "Content",
    pageId: "ContentSummary",
  },
  {
    path: "affiliate/",
    element: <AffiliateLanding />,
    pageType: "Affiliate",
    pageId: "AffiliateSummary",
  },
  { path: "clubinvites/", element: <ClubInvitesLanding /> },
  {
    path: "trending/",
    element: <TrendingLanding />,
    pageType: "Trending",
    pageId: "Trending",
  },
  {
    path: "toppicks/",
    element: <TopPickLanding />,
    pageType: "TopPicks",
    pageId: "TopPicks",
  },
  {
    path: "productreviews/",
    element: <ReviewLanding />,
    pageType: "Reviews",
    pageId: "ReviewsSummary",
  },
  {
    path: "recent/",
    element: <RecentlyViewedLanding />,
    pageType: "RecentlyViewed",
    pageId: "RecentlyViewed",
  },
  {
    path: "buyagain/",
    element: <BuyItAgainLanding />,
    pageType: "BuyItAgain",
    pageId: "BuyItAgain",
  },
  {
    path: "instantrewards/",
    element: <InstantRewardsLanding />,
    pageType: "Instant",
    pageId: "Instant",
  },
  ...departmentRoutes,
];

/**
 * Routes for app pages to be nested under AppMainPageOutlet
 */
export const appPageRoutes: AppRoutesWithChildrenProps[] = [
  ...commonAppPageRoutes,
  {
    path: "/",
    element: <HomeOutlet />,
    children: [
      { path: "", element: <Home />, pageType: "Home", pageId: "Home" },
    ],
  },
  {
    path: "shop/",
    element: <ShopOutlet />,
    children: [
      { path: "", element: <Shop />, pageType: "Shop", pageId: "Shop" },
    ],
  },
  {
    path: "search/",
    element: <SearchOutlet />,
    children: [
      {
        path: "",
        element: <Search />,
        pageType: "SearchResultsPage",
        pageId: "SearchResultsPage",
      },
    ],
  },
  {
    path: "concierge/",
    element: <ConciergeOutlet />,
    children: [
      {
        path: "",
        element: <Concierge />,
        pageType: "Concierge",
        pageId: "Concierge",
      },
      {
        path: "history",
        element: <ChatHistory />,
        pageType: "Concierge",
        pageId: "ChatHistory",
      },
    ],
  },
];

/**
 * Routes for custom brandstore app pages to be nested under AppMainPageOutlet
 */
export const customBrandpageAppRoutes: AppRoutesWithChildrenProps[] = [
  ...commonAppPageRoutes,
  {
    path: ":title/p/:sku/*",
    element: <BrandStoreLandingPage />,
    pageType: "Catalog",
    pageId: ":sku",
  },
  {
    path: "*",
    element: <BrandStoreLandingPage />,
    pageType: "BrandStoreSummary",
    pageId: "BrandStoreSummary",
  },
];
